import React, { useState, useEffect } from 'react';
import { Label } from 'reactstrap';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';

import BackButton from '../custom/BackButton';
import ActionIcon from '../custom/ActionIcon';
import { createdUpdatedInfo, formatDatetime } from '../../utils';
import { IEmployeeFile } from '../../globals/interfaces';
import RenderFile from './RenderFile';

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    file: IEmployeeFile | undefined;
    deleteHrFile: () => Promise<void>;
}

export default function ViewFile({ modal, setModal, file, deleteHrFile }: Props) {
    const [fileBase64, setFileBase64] = useState('');
    const [fileBlobUrl, setFileBlobUrl] = useState('');

    useEffect(() => {
        if (!file || !file.accessLink) return;

        fetch(file.accessLink)
            .then((response) => response.blob())
            .then((blob) => {
                const blobUrl = URL.createObjectURL(blob);
                setFileBlobUrl(blobUrl);

                // Cleanup the blob URL when the component unmounts
                return () => URL.revokeObjectURL(blobUrl);
            })
            .catch((error) => console.error('Error fetching file:', error));
    }, [file]);

    const handlePrint = () => {
        if (fileBase64) {
            const printWindow = window.open('', '_blank');
            if (!printWindow) return;
            const imageElement = printWindow.document.createElement('img');

            imageElement.onload = function () {
                printWindow.document.body.appendChild(imageElement);
                printWindow.print();
            };

            imageElement.src = fileBase64;
            printWindow.document.close();
        }
    };

    const handleDownload = () => {
        if (!fileBlobUrl || !file) return;
    
        const link = document.createElement('a');
        link.href = fileBlobUrl;
        link.download = file.s_file_name || 'download';
        document.body.appendChild(link); // Required for Firefox
        link.click();
        document.body.removeChild(link);
    };

    const toggle = () => setModal(!modal);

    if (!file) return null;

    let subtitle = '';

    if (file.expiration_date) {
        subtitle += `- Exp. ${formatDatetime(file.expiration_date, true)}`;
    }
    if (file.reminder_date) {
        subtitle += ` Renew by ${formatDatetime(file.reminder_date, true)}`;
    }

    const imageFile = checkImageFile(file.s_file_name);

    return (
        <Modal isOpen={modal} toggle={toggle} size={'lg'}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>
                        {file.s_file_type}
                        {subtitle}
                    </h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody className={'text-center'}>
                <RenderFile 
                    file={file}
                    fileBase64={fileBlobUrl}
                />
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <Label>
                        {createdUpdatedInfo(file)}
                    </Label>
                    <FooterButtonsContainer>
                        <ActionIcon type={'delete'} onClick={() => deleteHrFile()} />
                        <FlexContainer>
                            <ActionIcon type={'download'} onClick={() => handleDownload()} />
                            <ActionIcon
                                type={'print'}
                                onClick={() => handlePrint()}
                                disabled={!fileBase64 || !imageFile}
                            />
                        </FlexContainer>
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </Modal>
    );
}

const checkImageFile = (name: string) => {
    const images = ['jpeg', 'jpg', 'png'];
    for (const img of images) {
        if (name.includes(img)) {
            return true;
        }
    }
    return false;
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
`;

const FlexContainer = styled.div`
    display: flex;
    gap: 10px;
`;

const Img = styled.img`
    width: 700px;
    height: auto;
`;